/**
 * Design System Color Tokens
 * Using semantic naming for consistency and maintainability
 */

// Base palette - raw color values
const palette = {
  // Neutrals
  white: '#FFFFFF',
  black: '#000000',
  gray: {
    25: '#F8FAFC',      
    50: '#FAFBFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
  },
  
  // Brand colors - Stripe-inspired purple
  blue: {
    50: '#EFF6FF',
    100: '#DBEAFE',
    200: '#BFDBFE',
    300: '#93C5FD',
    400: '#60A5FA',
    500: '#3B82F6',
    600: '#2563EB',
    700: '#1D4ED8',
    800: '#1E40AF',
    900: '#1E3A8A',
  },
  
  // Primary brand purple (Stripe-inspired)
  indigo: {
    50: '#F0F9FF',
    100: '#E0E7FF',
    200: '#C7D2FE',
    300: '#A5B4FC',
    400: '#818CF8',
    500: '#6366F1',
    600: '#4F46E5',
    700: '#4338CA',
    800: '#3730A3',
    900: '#312E81',
  },
  
  // Feedback colors
  green: {
    50: '#F0FDF4',
    100: '#DCFCE7',
    200: '#BBF7D0',
    300: '#86EFAC',
    400: '#4ADE80',
    500: '#22C55E',
    600: '#16A34A',
    700: '#15803D',
    800: '#166534',
    900: '#14532D',
  },
  
  red: {
    50: '#FEF2F2',
    100: '#FEE2E2',
    200: '#FECACA',
    300: '#FCA5A5',
    400: '#F87171',
    500: '#EF4444',
    600: '#DC2626',
    700: '#B91C1C',
    800: '#991B1B',
    900: '#7F1D1D',
  },
  
  amber: {
    50: '#FFFBEB',
    100: '#FEF3C7',
    200: '#FDE68A',
    300: '#FCD34D',
    400: '#FBBF24',
    500: '#F59E0B',
    600: '#D97706',
    700: '#B45309',
    800: '#92400E',
    900: '#78350F',
  },
  
  purple: {
    50: '#FAF5FF',
    100: '#F3E8FF',
    200: '#E9D5FF',
    300: '#D8B4FE',
    400: '#C084FC',
    500: '#A855F7',
    600: '#9333EA',
    700: '#7C3AED',
    800: '#6B21A8',
    900: '#581C87',
  },
};

// Semantic color tokens
export const colors = {
  // Background colors
  background: {
    primary: palette.white,
    primsec: palette.gray[25],
    secondary: palette.gray[50],
    tertiary: palette.gray[100],
    inverse: palette.gray[900],
    
    // Interactive backgrounds
    hover: palette.gray[50],
    active: palette.gray[100],
    selected: palette.blue[50],
    disabled: palette.gray[100],
  },
  
  // Text colors
  text: {
    primary: '#222429',              // Softer almost-black as requested
    secondary: palette.gray[600],
    tertiary: palette.gray[500],
    inverse: palette.white,
    disabled: palette.gray[400],
    
    // State text
    error: palette.red[700],
    warning: palette.amber[700],
    success: palette.green[700],
    info: palette.blue[700],
  },
  
  // Border colors
  border: {
    primary: palette.gray[200],
    secondary: palette.gray[100],
    focus: palette.blue[500],
    error: palette.red[300],
    
    // Interactive borders
    hover: palette.gray[300],
    active: palette.gray[400],
  },
  
  // Brand colors
  brand: {
    primary: palette.indigo[600],
    primaryHover: palette.indigo[700],
    primaryActive: palette.indigo[800],
    primarySurface: palette.indigo[50],
    
    secondary: palette.purple[600],
    secondaryHover: palette.purple[700],
    secondaryActive: palette.purple[800],
    secondarySurface: palette.purple[50],
  },
  
  // Feedback colors
  feedback: {
    error: {
      primary: palette.red[600],
      surface: palette.red[50],
      border: palette.red[200],
      text: palette.red[700],
    },
    warning: {
      primary: palette.amber[600],
      surface: palette.amber[50],
      border: palette.amber[200],
      text: palette.amber[700],
    },
    success: {
      primary: palette.green[600],
      surface: palette.green[50],
      border: palette.green[200],
      text: palette.green[700],
    },
    info: {
      primary: palette.blue[600],
      surface: palette.blue[50],
      border: palette.blue[200],
      text: palette.blue[700],
    },
  },
  
  // Component-specific
  button: {
    primary: {
      background: palette.indigo[600],
      backgroundHover: palette.indigo[700],
      backgroundActive: palette.indigo[800],
      text: palette.white,
    },
    secondary: {
      background: palette.white,
      backgroundHover: palette.gray[50],
      backgroundActive: palette.gray[100],
      text: palette.gray[700],
      border: palette.gray[300],
    },
    ghost: {
      background: 'transparent',
      backgroundHover: palette.gray[50],
      backgroundActive: palette.gray[100],
      text: palette.gray[700],
    },
    danger: {
      background: palette.red[600],
      backgroundHover: palette.red[700],
      backgroundActive: palette.red[800],
      text: palette.white,
    },
  },
  
  // Raw palette for edge cases
  palette,
}; 